import { useState, useRef, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Bot, Send, X, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_TYPES = [
  { v: "all", l: "Alla i organisationen" },
  { v: "department", l: "Avdelning" },
  { v: "team", l: "Team" },
  { v: "shift", l: "Skift" },
  { v: "individual", l: "Enskild person" },
];

function AudiencePicker({ value, onChange, teams, shifts, departments, employees }) {
  const type = value?.type || "all";
  const ids = value?.ids || [];
  const empOpts = (employees || []).map((e) => ({ id: e.id, name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email || e.id }));
  const opts = type === "department" ? departments : type === "team" ? teams : type === "shift" ? shifts : type === "individual" ? empOpts : [];
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs">Mottagare</Label>
        <Select value={type} onValueChange={(v) => onChange({ type: v, ids: [] })}>
          <SelectTrigger data-testid="aud-type"><SelectValue /></SelectTrigger>
          <SelectContent>{AUDIENCE_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {type !== "all" && (
        <div>
          <Label className="text-xs">Välj</Label>
          <Select value={ids[0] || ""} onValueChange={(v) => onChange({ type, ids: [v] })}>
            <SelectTrigger data-testid="aud-target"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{(opts || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function ConfirmCard({ action, refs, onExecuted, onCancel }) {
  const [p, setP] = useState(() => ({ ...(action.params || {}) }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      const payload = { ...p };
      if (action.type === "create_survey" && typeof p._questionsText === "string") {
        payload.questions = p._questionsText.split("\n").map((t) => t.trim()).filter(Boolean)
          .map((t, i) => ({ id: `q${i + 1}`, text: t, type: "text" }));
        delete payload._questionsText;
      }
      const r = await api.post("/assistant/execute", { type: action.type, params: payload });
      onExecuted(r.data.message || "Klart.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 rounded-lg border bg-card p-3 shadow-sm" data-testid="assistant-confirm">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-accent">
        <Sparkles className="h-3.5 w-3.5" />Förslag – granska och bekräfta
      </div>

      {action.type === "report_sick" && (
        <div className="grid gap-2">
          <div><Label className="text-xs">Sjuk från</Label>
            <Input type="datetime-local" value={p.start_time || ""} onChange={(e) => set("start_time", e.target.value)} data-testid="c-sick-start" /></div>
          <div><Label className="text-xs">Förväntad återgång (valfritt)</Label>
            <Input type="date" value={p.expected_return || ""} onChange={(e) => set("expected_return", e.target.value)} data-testid="c-sick-return" /></div>
        </div>
      )}

      {action.type === "request_leave" && (
        <div className="grid gap-2">
          <div><Label className="text-xs">Typ</Label>
            <Select value={p.type || "semester"} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="c-leave-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="semester">Semester</SelectItem><SelectItem value="tjänstledig">Tjänstledighet</SelectItem><SelectItem value="föräldraledig">Föräldraledighet</SelectItem><SelectItem value="annat">Annat</SelectItem></SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Från</Label><Input type="date" value={p.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="c-leave-start" /></div>
            <div><Label className="text-xs">Till</Label><Input type="date" value={p.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="c-leave-end" /></div>
          </div>
          <div><Label className="text-xs">Kommentar</Label><Input value={p.comment || ""} onChange={(e) => set("comment", e.target.value)} /></div>
        </div>
      )}

      {action.type === "send_broadcast" && (
        <div className="grid gap-2">
          <div><Label className="text-xs">Rubrik (valfritt)</Label><Input value={p.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="c-bc-title" /></div>
          <div><Label className="text-xs">Meddelande</Label><Textarea rows={3} value={p.message || ""} onChange={(e) => set("message", e.target.value)} data-testid="c-bc-msg" /></div>
          <AudiencePicker value={p.audience || { type: "all" }} onChange={(a) => set("audience", a)} {...refs} />
        </div>
      )}

      {action.type === "create_survey" && (
        <div className="grid gap-2">
          <div><Label className="text-xs">Titel</Label><Input value={p.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="c-sv-title" /></div>
          <div><Label className="text-xs">Beskrivning</Label><Input value={p.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div><Label className="text-xs">Frågor (en per rad)</Label>
            <Textarea rows={3} value={p._questionsText ?? (p.questions || []).map((q) => q.text).join("\n")} onChange={(e) => set("_questionsText", e.target.value)} data-testid="c-sv-questions" /></div>
          <AudiencePicker value={p.audience || { type: "all" }} onChange={(a) => set("audience", a)} {...refs} />
          <p className="text-[11px] text-muted-foreground">Enkäten skapas som utkast och skickas till godkännande innan den publiceras.</p>
        </div>
      )}

      {action.type === "post_feed" && (
        <div className="grid gap-2">
          <div><Label className="text-xs">Rubrik (valfritt)</Label><Input value={p.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="c-feed-title" /></div>
          <div><Label className="text-xs">Kategori</Label>
            <Select value={p.category || "nyhet"} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="c-feed-cat"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="rekord">Rekord</SelectItem><SelectItem value="nyhet">Nyhet</SelectItem><SelectItem value="halsning">Hälsning</SelectItem><SelectItem value="ovrigt">Övrigt</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-xs">Innehåll</Label><Textarea rows={3} value={p.body || ""} onChange={(e) => set("body", e.target.value)} data-testid="c-feed-body" /></div>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={run} disabled={busy} data-testid="assistant-confirm-btn"><CheckCircle2 className="mr-1 h-4 w-4" />{busy ? "Skickar…" : "Bekräfta"}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="assistant-cancel-btn">Avbryt</Button>
      </div>
    </div>
  );
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Hej! Jag kan hjälpa dig med sjukanmälan, ledighet, schema, enkäter, utskick och flödet. Vad vill du göra?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const { user } = useAuth();
  const canSeePeople = (user?.access_level ?? 1) >= 3;
  const { data: teams } = useApi("/teams");
  const { data: shifts } = useApi("/shifts");
  const { data: departments } = useApi("/departments");
  const { data: employees } = useApi(canSeePeople ? "/employees" : null);
  const refs = { teams, shifts, departments, employees };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const history = msgs.filter((m) => !m.action).map((m) => ({ role: m.role, content: m.content }));
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await api.post("/assistant/chat", { message: text, history });
      const action = r.data.action;
      if (action?.type === "my_schedule") {
        const ex = await api.post("/assistant/execute", { type: "my_schedule", params: {} });
        setMsgs((m) => [...m, { role: "assistant", content: ex.data.message }]);
      } else {
        setMsgs((m) => [...m, { role: "assistant", content: r.data.reply, action }]);
      }
    } catch (e) { setMsgs((m) => [...m, { role: "assistant", content: apiErr(e) }]); }
    finally { setBusy(false); }
  };

  const onExecuted = (idx, message) => {
    setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, action: null, done: true } : x)).concat([{ role: "assistant", content: message }]));
  };
  const onCancel = (idx) => setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, action: null } : x)));

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="assistant-fab"
        className="fixed bottom-20 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg transition-transform hover:scale-105 active:scale-95 sm:bottom-6 sm:right-5">
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      {open && (
        <div data-testid="assistant-panel"
          className="fixed bottom-36 right-4 z-50 flex h-[68vh] max-h-[560px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl sm:bottom-24 sm:right-5">
          <div className="flex items-center gap-2 border-b bg-primary px-4 py-3 text-primary-foreground">
            <Sparkles className="h-5 w-5" />
            <div className="font-head text-sm font-bold">Assistent</div>
            <Badge variant="secondary" className="ml-auto text-[10px]">Bekräftar alltid</Badge>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-3">
            {msgs.map((m, i) => (
              <div key={i}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "ml-auto bg-accent text-accent-foreground" : "bg-muted"}`}>
                  <span className="whitespace-pre-wrap">{m.content}</span>
                </div>
                {m.action && <ConfirmCard action={m.action} refs={refs} onExecuted={(msg) => onExecuted(i, msg)} onCancel={() => onCancel(i)} />}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="flex items-center gap-2 border-t p-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Skriv här…" data-testid="assistant-input" />
            <Button size="icon" onClick={send} disabled={busy} data-testid="assistant-send"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
